import { EmojiPicker, useEmojiFavourites } from "@norkron/web-emoji-picker";
import { Popover, PopoverContent, PopoverTrigger } from "@norkron/web-popover";
import { useEffect, useRef, useState } from "react";
import type { ReactionGroup } from "../../api/types";
import { SmilePlus } from "../../compose/Icon";
import { actorFromLegacy } from "../../lib/avatar";
import { useRestOutbox } from "../../sync/useRestOutbox";
import { Avatar } from "../shared/Avatar";

/**
 * review-notes §4.4/§4.7: the Slack-style reaction bar under a comment/reply.
 *   · one chip per distinct emoji (`emoji + count`), YOUR reactions in a distinct
 *     accent color (`data-reacted="true"` + `aria-pressed`), click to toggle;
 *   · a trailing `+` button opens the full `@norkron/web-emoji-picker`
 *     `<EmojiPicker>` (9-group category rail, keyword search, skin-tone,
 *     recents — RULE ZERO: no hand-rolled emoji grid), mounted inside a
 *     `@norkron/web-popover` `PopoverContent` so it PORTALS out of the review
 *     rail's overflow clip and positions `fixed` against the `+` trigger, with
 *     outside-click/Esc dismissal composed via web-dismiss (nothing hand-rolled);
 *   · HOVERING the `+` reveals a small favourites PILL — the user's ≤5 pinned
 *     emoji (from web-emoji-picker's ⋯-settings favourites store) for one-click
 *     quick-react, without opening the full picker (design §4.6);
 *   · a who-reacted popup (avatars + names) opens on a 2-SECOND hover OR an
 *     immediate right-click (contextmenu) when an emoji has more than one reactor;
 *   · reactions are OPTIMISTIC + durable: `onToggleReaction` flips the chip now and
 *     routes through the REST-replay outbox, so a chip queued OFFLINE shows a
 *     "will sync" affordance instead of failing;
 *   · SSE live-update is owned by the parent (it patches the comment's `reactions`
 *     prop); this component is presentational + calls `onToggleReaction`.
 *
 * Behind a clean boundary so the whole bar + picker + popup can be extracted to
 * `@norkron/web-reactions` (design §4.6 FR).
 */

/** ms the pointer must dwell on a chip before the who-popup opens (§4.7). */
const HOVER_OPEN_MS = 2000;
/** grace after mouseleave so the pointer can travel into the popup (§4.7). */
const HOVER_CLOSE_MS = 150;
/** grace after leaving the `+` so the pointer can travel into the favourites pill. */
const FAV_CLOSE_MS = 150;
/** The web-emoji-picker storage namespace elaborate's picker uses (its default),
 *  so the pill reads the SAME favourites the ⋯-settings editor writes. */
const EMOJI_STORAGE_KEY = "emoji-picker";
/** The quick-react pill shows at most this many favourites (picker capacity is 5). */
const FAV_PILL_MAX = 5;

export function ReactionBar({
  cid,
  reactions,
  onToggleReaction,
  compact = false,
}: {
  cid: number;
  reactions: ReactionGroup[];
  /** Toggle the viewer's `emoji` reaction; `add` true ⇒ react, false ⇒ un-react. */
  onToggleReaction: (cid: number, emoji: string, add: boolean) => void;
  /** The lighter reply variant (rendered under a `.thread-comment`). */
  compact?: boolean;
}) {
  const [pickerOpen, setPickerOpen] = useState(false);
  // The emoji whose who-reacted popup is currently open (one at a time).
  const [openPopup, setOpenPopup] = useState<string | null>(null);
  const [favOpen, setFavOpen] = useState(false);
  const openTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const favTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // The viewer's pinned favourites (web-emoji-picker's persisted base graphemes).
  const { favouriteChars } = useEmojiFavourites(EMOJI_STORAGE_KEY, true);
  const favourites = favouriteChars.slice(0, FAV_PILL_MAX);

  // Unsynced reaction keys → the per-chip "queued / will sync" affordance.
  const { pending } = useRestOutbox();

  const clearTimers = () => {
    if (openTimer.current) clearTimeout(openTimer.current);
    if (closeTimer.current) clearTimeout(closeTimer.current);
    openTimer.current = null;
    closeTimer.current = null;
  };
  // Clear any pending timers on unmount (no setState after teardown).
  useEffect(
    () => () => {
      clearTimers();
      if (favTimer.current) clearTimeout(favTimer.current);
    },
    [],
  );

  // Outside-click + Esc dismiss for the open popup (the shared review-card
  // pattern). Only bound while a popup is open.
  useEffect(() => {
    if (openPopup == null) return;
    const onPointerDown = (e: PointerEvent) => {
      const el = e.target as HTMLElement;
      if (!el.closest(`[data-reaction-scope="${cid}"]`)) setOpenPopup(null);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpenPopup(null);
    };
    document.addEventListener("pointerdown", onPointerDown, true);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown, true);
      document.removeEventListener("keydown", onKey);
    };
  }, [openPopup, cid]);

  const hoverOpen = (emoji: string, hasPopup: boolean) => {
    if (!hasPopup) return;
    clearTimers();
    openTimer.current = setTimeout(() => setOpenPopup(emoji), HOVER_OPEN_MS);
  };
  const hoverLeave = () => {
    if (openTimer.current) clearTimeout(openTimer.current);
    openTimer.current = null;
    closeTimer.current = setTimeout(() => setOpenPopup(null), HOVER_CLOSE_MS);
  };
  const cancelClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = null;
  };

  // Favourites pill hover lifecycle (open on `+` hover/focus, grace-close so the
  // pointer can travel into the pill). Only meaningful when favourites exist.
  const favEnter = () => {
    if (favourites.length === 0) return;
    if (favTimer.current) clearTimeout(favTimer.current);
    favTimer.current = null;
    setFavOpen(true);
  };
  const favLeave = () => {
    if (favTimer.current) clearTimeout(favTimer.current);
    favTimer.current = setTimeout(() => setFavOpen(false), FAV_CLOSE_MS);
  };

  const toggle = (emoji: string, reacted: boolean) => {
    setOpenPopup(null);
    setPickerOpen(false);
    setFavOpen(false);
    onToggleReaction(cid, emoji, !reacted);
  };

  // Only distinct emoji with at least one reactor render a chip.
  const groups = reactions.filter((g) => g.count > 0);

  const addButton = (
    <button
      type="button"
      className="reaction-add"
      data-testid={`reaction-add-${cid}`}
      aria-label="Add a reaction"
      title="Add a reaction"
    >
      <SmilePlus size={compact ? 13 : 14} strokeWidth={1.6} />
    </button>
  );

  return (
    <div
      className={`reaction-bar${compact ? " reaction-bar--compact" : ""}`}
      data-testid={`reaction-bar-${cid}`}
      data-reaction-scope={cid}
    >
      {groups.map((g) => {
        const hasPopup = g.who.length > 1;
        const names = g.who.map((u) => u.name).join(", ");
        const queued = pending.has(`reaction:${cid}:${g.emoji}`);
        return (
          <span key={g.emoji} className="reaction-chip__wrap">
            <button
              type="button"
              className="reaction-chip"
              data-testid={`comment-reaction-${g.emoji}-${cid}`}
              data-reacted={g.reacted_by_me ? "true" : "false"}
              data-queued={queued ? "true" : "false"}
              aria-pressed={g.reacted_by_me}
              aria-label={
                (hasPopup
                  ? `${g.emoji} ${g.count} reactions: ${names} — press to toggle`
                  : `${g.emoji} ${g.count} reaction${g.count === 1 ? "" : "s"}${names ? `: ${names}` : ""} — press to toggle`) +
                (queued ? " (queued, will sync)" : "")
              }
              onClick={() => toggle(g.emoji, g.reacted_by_me)}
              onMouseEnter={() => hoverOpen(g.emoji, hasPopup)}
              onMouseLeave={hoverLeave}
              onContextMenu={(e) => {
                if (!hasPopup) return;
                e.preventDefault();
                clearTimers();
                setOpenPopup(g.emoji);
              }}
            >
              <span className="reaction-chip__emoji" aria-hidden="true">
                {g.emoji}
              </span>
              <span className="reaction-chip__count">{g.count}</span>
              {queued ? (
                <span
                  className="reaction-chip__queued"
                  data-testid={`reaction-chip-queued-${g.emoji}-${cid}`}
                  aria-hidden="true"
                  title="Queued — will sync when you're back online"
                />
              ) : null}
            </button>
            {openPopup === g.emoji && hasPopup ? (
              // biome-ignore lint/a11y/noStaticElementInteractions: pointer grace-keeper so the popup doesn't close mid-travel; dismissal is outside-click + Esc.
              <div
                className="reaction-users-popup"
                role="dialog"
                aria-label={`Reacted with ${g.emoji}`}
                data-testid={`reaction-users-popup-${g.emoji}-${cid}`}
                onMouseEnter={cancelClose}
                onMouseLeave={hoverLeave}
              >
                {g.who.map((u) => (
                  <div
                    key={u.user_id}
                    className="reaction-users-popup__row"
                    data-testid={`reaction-user-${u.user_id}-${g.emoji}-${cid}`}
                  >
                    <Avatar actor={actorFromLegacy(u.name)} size="xs" />
                    <span className="reaction-users-popup__name">{u.name}</span>
                  </div>
                ))}
              </div>
            ) : null}
          </span>
        );
      })}

      <span className="reaction-chip__wrap">
        {/* Favourites hover pill (outer Popover, hover/focus-controlled) wraps the
            `+` trigger so it PORTALS out of the rail's overflow clip, exactly like
            the picker. Its trigger is a `<span>` wrapper (NOT the button) so it
            never collides with the inner picker Popover's `asChild` on the button.
            When no favourites are pinned the wrapper is absent → hover is a no-op
            and only the full picker (on click) remains. */}
        {favourites.length > 0 ? (
          // biome-ignore lint/a11y/noStaticElementInteractions: hover host for the favourites pill; the buttons inside (and the picker) are the interactive targets.
          <span
            className="reaction-add-favhost"
            onMouseEnter={favEnter}
            onMouseLeave={favLeave}
            onFocus={favEnter}
            onBlur={favLeave}
          >
            <Popover open={favOpen} onOpenChange={setFavOpen} placement="top">
              <PopoverTrigger asChild>
                <span className="reaction-add-anchor">
                  <Popover
                    open={pickerOpen}
                    onOpenChange={setPickerOpen}
                    placement="top-start"
                  >
                    <PopoverTrigger asChild>{addButton}</PopoverTrigger>
                    <PopoverContent role="dialog" className="reaction-emoji-pop">
                      <EmojiPicker
                        idSuffix={`-${cid}`}
                        aria-label="Add a reaction"
                        onPick={(emoji) => {
                          const existing = reactions.find((g) => g.emoji === emoji);
                          toggle(emoji, existing?.reacted_by_me ?? false);
                        }}
                      />
                    </PopoverContent>
                  </Popover>
                </span>
              </PopoverTrigger>
              <PopoverContent
                role="menu"
                aria-label="Quick react with a favourite"
                className="reaction-fav-pill"
                data-testid="reaction-fav-pill"
              >
                <div className="reaction-fav-pill__row">
                  {favourites.map((char) => {
                    const existing = reactions.find((g) => g.emoji === char);
                    return (
                      <button
                        key={char}
                        type="button"
                        role="menuitem"
                        className="reaction-fav-pill__btn"
                        data-testid={`reaction-fav-${char}`}
                        aria-label={`Quick react with ${char}`}
                        title={char}
                        onClick={() =>
                          toggle(char, existing?.reacted_by_me ?? false)
                        }
                      >
                        <span aria-hidden="true">{char}</span>
                      </button>
                    );
                  })}
                </div>
              </PopoverContent>
            </Popover>
          </span>
        ) : (
          // No favourites pinned → the plain picker-on-click, no hover pill.
          <Popover
            open={pickerOpen}
            onOpenChange={setPickerOpen}
            placement="top-start"
          >
            <PopoverTrigger asChild>{addButton}</PopoverTrigger>
            <PopoverContent role="dialog" className="reaction-emoji-pop">
              <EmojiPicker
                idSuffix={`-${cid}`}
                aria-label="Add a reaction"
                onPick={(emoji) => {
                  const existing = reactions.find((g) => g.emoji === emoji);
                  toggle(emoji, existing?.reacted_by_me ?? false);
                }}
              />
            </PopoverContent>
          </Popover>
        )}
      </span>
    </div>
  );
}
